import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { clearTokens, getAccessToken } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/ui";
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { BrandsPage } from "@/pages/BrandsPage";
import { ConnectPage } from "@/pages/ConnectPage";
import { VoicePage } from "@/pages/VoicePage";
import { CalendarPage } from "@/pages/CalendarPage";
import { SchedulePage } from "@/pages/SchedulePage";
import { CampaignsPage } from "@/pages/CampaignsPage";
import { PlanMonthPage } from "@/pages/PlanMonthPage";
import { PhotoPostPage } from "@/pages/PhotoPostPage";
import { ApprovalsPage } from "@/pages/ApprovalsPage";
import { BillingPage } from "@/pages/BillingPage";
import { AccountPage } from "@/pages/AccountPage";
import {
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "@/pages/PasswordPages";
import { DataDeletionPage, PrivacyPolicyPage } from "@/pages/LegalPages";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

/**
 * Gate for authenticated routes. Resolves /auth/me once so the workspace
 * switcher and every scoped request have a workspace id before any child
 * query runs.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const hasToken = !!getAccessToken();
  const me = useMe();

  if (!hasToken) return <Navigate to="/login" replace state={{ from: location }} />;
  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-sm text-muted">
        <Spinner />
        {t("errors.loading")}
      </div>
    );
  }
  if (me.isError) {
    clearTokens();
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Every signed-in route gets the same frame, so it is applied once here. */
function Protected({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppShell>{children}</AppShell>
    </RequireAuth>
  );
}

/**
 * Route order mirrors the sidebar's order, which mirrors the order the work
 * is actually done in. "/" is the brands screen because with no brand there
 * is nothing else the product can do.
 */
const ROUTES: Array<{ path: string; element: React.ReactNode }> = [
  { path: "/", element: <BrandsPage /> },
  { path: "/brands", element: <BrandsPage /> },
  { path: "/connect", element: <ConnectPage /> },
  { path: "/voice", element: <VoicePage /> },
  { path: "/photo", element: <PhotoPostPage /> },
  { path: "/campaigns", element: <CampaignsPage /> },
  { path: "/plan-month", element: <PlanMonthPage /> },
  { path: "/approvals", element: <ApprovalsPage /> },
  { path: "/calendar", element: <CalendarPage /> },
  { path: "/schedule", element: <SchedulePage /> },
  { path: "/billing", element: <BillingPage /> },
  { path: "/account", element: <AccountPage /> },
];

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* Reached from an email or from the sign-in page: no session, so
              they sit outside Protected. verify-email is public because the
              whole point is that it works from whatever browser opened the
              mail, which is often not the one that signed up. */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Public, and they must stay public: Meta's reviewer and crawler
              fetch both without a session, and an app review fails on a
              privacy policy URL that redirects to a sign-in screen. These are
              also the URLs configured in the Meta app dashboard, so renaming
              one is a thing someone then has to go and change there. */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          {ROUTES.map(({ path, element }) => (
            <Route key={path} path={path} element={<Protected>{element}</Protected>} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}
